import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/The Cutaway";

export default function TheCutawayPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const partTypeFrames = Array.from({ length: 15 }, (_, i) =>
    `${BASE}/Part Types/Frame_${String(i).padStart(5, "0")}.png`
  );
  const partTypeCaps = partTypeFrames.map((_, i) => `Type ${String(i + 1).padStart(2, "0")}`);

  const detail1 = [
    `${BASE}/Details/d1/top technical.jpg`,
    `${BASE}/Details/d1/1.jpg`,
    `${BASE}/Details/d1/2.jpg`,
    `${BASE}/Details/d1/3.jpg`,
    `${BASE}/Details/d1/4.jpg`,
    `${BASE}/Details/d1/5.jpg`,
    `${BASE}/Details/d1/6.jpg`,
  ];
  const detail2 = [
    `${BASE}/Details/d2/top technical.jpg`,
    `${BASE}/Details/d2/1.jpg`,
    `${BASE}/Details/d2/2.jpg`,
    `${BASE}/Details/d2/3.jpg`,
    `${BASE}/Details/d2/4.jpg`,
    `${BASE}/Details/d2/5.jpg`,
  ];
  const detailCaps = (arr) => arr.map((_, i) => (i === 0 ? "Overview" : `Step ${String(i).padStart(2, "0")}`));

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="The Cutaway"
          description="A computational timber-structure study for a civic anchor at ground level in Barangaroo, Sydney — parametric part-typing applied to a real construction sequence, from abstract model to on-site assembly."
          meta={["Barangaroo, Australia", "Rhino", "Grasshopper", "2024"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Card_ext render.jpeg`}
                alt="The Cutaway — exterior render"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Project orientation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The Cutaway is a civic timber structure at the edge of Barangaroo in Sydney. The brief asked for a parametric pipeline that could resolve a complex geometry into a discrete set of part types — each type repeatable enough to fabricate cleanly, each instance specific enough to adapt to the surrounding structure.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Rendered abstract model.jpg`} alt="Abstract structural model" ratio="classic" fit="cover" caption="Abstract model" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/enterance exterior render.jpg`} alt="Entrance exterior render" ratio="classic" fit="cover" caption="Entrance view" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Component part types">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The structural system resolves to a finite catalogue of part types, each addressing a specific functional and geometric condition. The animated sequence steps through the catalogue — showing how the parts integrate into the global structure.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={partTypeFrames}
                  captions={partTypeCaps}
                  label="Part type catalogue"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Technical details">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Two assembly families — d1 and d2 — captured as sequential build-ups from the technical overview through each connection step.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <ImageSequence
                  images={detail1}
                  captions={detailCaps(detail1)}
                  label="Detail · d1"
                  ratio="classic"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <ImageSequence
                  images={detail2}
                  captions={detailCaps(detail2)}
                  label="Detail · d2"
                  ratio="classic"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Construction process">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Construction process1.png`} alt="Construction — stage 1" ratio="square" fit="cover" caption="Stage 01" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Construction process2.png`} alt="Construction — stage 2" ratio="square" fit="cover" caption="Stage 02" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Construction process3.png`} alt="Construction — stage 3" ratio="square" fit="cover" caption="Stage 03" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Construction process4.png`} alt="Construction — stage 4" ratio="square" fit="cover" caption="Stage 04" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure src={`${BASE}/Construction process5.png`} alt="Construction — stage 5" ratio="square" fit="cover" caption="Stage 05" />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Construction process6.png`} alt="Construction — stage 6" ratio="square" fit="cover" caption="Stage 06" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/exterior construction image.png`} alt="Exterior construction view" ratio="banner" fit="cover" caption="Site view" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Architectural visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/render interior floor .jpg`} alt="Interior — floor render" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/render interior balcony.jpg`} alt="Interior — balcony render" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
